import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { MailIcon, MailOpenIcon, RotateCcwIcon, ShuffleIcon } from "lucide-react";
import { DrawNav } from "../components/DrawNav";
import { NameRoster } from "../components/NameRoster";
import { RevealOverlay } from "../components/RevealOverlay";
import { RevealedList } from "../components/RevealedList";
import { CopyButton } from "../components/CopyButton";
import { Envelope } from "../components/Envelope";
import { TeamLetter } from "../components/TeamLetter";
import { useReveal } from "../hooks/useReveal";
import { defaultLinkNames, defaultMahindraNames, schools } from "../data/participants";
import { findDuplicates, makeTeams, teamsToText } from "../utils/teams";
import type { Team } from "../types/team";
const STORAGE_KEY = "team-draw-names-v2";
function loadNames(): {
  link: string[];
  mahindra: string[];
} {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as {
        link?: string[];
        mahindra?: string[];
      };
      if (parsed.link?.length === 6 && parsed.mahindra?.length === 5) {
        return {
          link: parsed.link,
          mahindra: parsed.mahindra
        };
      }
    }
  } catch {
    /* ignore */
  }
  return {
    link: defaultLinkNames,
    mahindra: defaultMahindraNames
  };
}
export function TeamDraw() {
  const [linkNames, setLinkNames] = useState<string[]>(() => loadNames().link);
  const [mahindraNames, setMahindraNames] = useState<string[]>(() => loadNames().mahindra);
  const [teams, setTeams] = useState<Team[] | null>(null);
  const [round, setRound] = useState(0);
  const [stale, setStale] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const reveal = useReveal(teams?.length ?? 0);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      link: linkNames,
      mahindra: mahindraNames
    }));
  }, [linkNames, mahindraNames]);
  const duplicates = useMemo(() => findDuplicates([...linkNames, ...mahindraNames]), [linkNames, mahindraNames]);
  const emptyCount = [...linkNames, ...mahindraNames].filter(n => !n.trim()).length;
  const error = emptyCount > 0 ? `${emptyCount} name${emptyCount > 1 ? "s" : ""} missing` : duplicates.size > 0 ? "Each name must be unique" : null;
  const updateName = (school: "link" | "mahindra", index: number, value: string) => {
    const setter = school === "link" ? setLinkNames : setMahindraNames;
    setter(prev => prev.map((n, i) => i === index ? value : n));
    if (teams) setStale(true);
  };
  const handleShuffle = () => {
    if (error) return;
    setTeams(makeTeams(linkNames.map(n => n.trim()), mahindraNames.map(n => n.trim())));
    setRound(r => r + 1);
    setStale(false);
    reveal.reset();
    setOverlayOpen(true);
  };
  const handleReset = () => {
    setLinkNames(defaultLinkNames);
    setMahindraNames(defaultMahindraNames);
    setTeams(null);
    setStale(false);
    reveal.reset();
  };
  const closeOverlay = useCallback(() => setOverlayOpen(false), []);
  const currentTeam = teams?.[reveal.current];
  return <div className="min-h-full w-full bg-paper" data-id="element-1147" data-mp="1ay1g2qa46858">
      <main className="mx-auto max-w-6xl px-5 py-10 md:px-8 md:py-14" data-id="element-1148" data-mp="1pnpg7o6k70rd">
        <DrawNav data-id="element-1149" data-mp="1pp0nvz34ksnr" />
        <header className="mt-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-1150" data-mp="1kocrsz01hlmo">
          <div data-id="element-1151" data-mp="wya9halxfu19">
            <h1 className="text-3xl font-semibold tracking-tight md:text-4xl" data-id="element-1152" data-mp="wxqplocpuvkl">Team draw</h1>
            <p className="mt-2 max-w-lg text-[15px] text-muted" data-id="element-1153" data-mp="s08nohx49gm3">
              11 participants, 5 teams. Every pair mixes LINK and Mahindra; the trio takes two from LINK.
            </p>
          </div>
          <div className="flex flex-col items-start gap-2 md:items-end" data-id="element-1154" data-mp="11xdbem6m1k50">
            <button type="button" onClick={handleShuffle} disabled={!!error} className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl bg-ink px-5 py-3 text-[15px] font-semibold text-white transition-[transform,background-color] duration-150 ease-out hover:bg-ink/90 active:scale-[0.97] disabled:cursor-not-allowed disabled:bg-ink/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2 focus-visible:ring-offset-paper" data-id="element-1155" data-mp="2hedjp6vpgi87">
              <ShuffleIcon className="h-4 w-4" aria-hidden="true" data-id="element-1156" data-mp="3mnj3ubnopbon" />
              {teams ? "Shuffle again" : "Shuffle teams"}
            </button>
            {error && <p className="text-sm text-danger" role="alert" data-id="element-1157" data-mp="3vmmkza3ajh0i">
                {error}
              </p>}
          </div>
        </header>

        <div className="mt-10 grid gap-10 lg:grid-cols-12" data-id="element-1158" data-mp="4atka6m846af">
          <div className="space-y-8 lg:col-span-5" data-id="element-1159" data-mp="96s3zgwlx04i">
            <NameRoster school="link" title={schools.link.name} country={schools.link.country} names={linkNames} duplicates={duplicates} onChange={(i, v) => updateName("link", i, v)} data-id="element-1160" data-mp="99o4nczua2wn" />
            <NameRoster school="mahindra" title={schools.mahindra.name} country={schools.mahindra.country} names={mahindraNames} duplicates={duplicates} onChange={(i, v) => updateName("mahindra", i, v)} data-id="element-1161" data-mp="23lyw1gbwxeba" />
            <button type="button" onClick={handleReset} className="inline-flex items-center gap-1.5 text-sm text-muted transition-colors duration-150 hover:text-ink" data-id="element-1162" data-mp="1osvqk7blir0x">
              <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1163" data-mp="2coqa7nw6ao6m" />
              Reset to saved names
            </button>
          </div>

          <section aria-labelledby="teams-heading" className="lg:col-span-7" data-id="element-1164" data-mp="27ovjbkcedylk">
            <h2 id="teams-heading" className="pb-3 text-[15px] font-semibold" data-id="element-1165" data-mp="355ar9fd6m9jz">
              Teams{teams && <span className="ml-2 font-normal text-muted" data-id="element-1166" data-mp="30dstt1adar3r">Round {round}</span>}
            </h2>
            {stale && <p className="mb-3 text-sm text-muted" data-id="element-1167" data-mp="yi3e6792fe9u">Names changed since this draw. Shuffle again to update.</p>}
            {!teams ? <div className="flex min-h-[420px] flex-col items-center justify-center rounded-2xl border border-dashed border-line px-6 text-center" data-id="element-1168" data-mp="oqyy147nlndf">
                <MailIcon className="h-6 w-6 text-muted" aria-hidden="true" data-id="element-1169" data-mp="tor0gs5xcrvv" />
                <p className="mt-4 text-[15px] font-medium" data-id="element-1170" data-mp="fq12zbf70q6z">No envelopes yet</p>
                <p className="mt-1 max-w-xs text-sm text-muted" data-id="element-1171" data-mp="jrmg8xvn36hp">
                  Shuffle to seal five teams into envelopes, then open them one by one.
                </p>
              </div> : <div className={`transition-opacity duration-200 ${stale ? "opacity-50" : ""}`} data-id="element-1172" data-mp="aqoaaa1n4owh">
                <div className="bg-waves flex flex-wrap items-center justify-between gap-4 rounded-2xl px-6 py-5 text-white" data-id="element-1173" data-mp="205mcnyva0e07">
                  <div data-id="element-1174" data-mp="253w7u9h03elt">
                    <p className="text-[15px] font-semibold" data-id="element-1175" data-mp="254fn1if9now5">
                      {reveal.allRevealed ? "All envelopes opened" : `${reveal.revealedCount} of ${teams.length} envelopes opened`}
                    </p>
                    <p className="text-sm text-white/60" data-id="element-1176" data-mp="38qawyd6au6z">Link × Mahindra · Round {round}</p>
                  </div>
                  <button type="button" onClick={() => {
                if (reveal.allRevealed) reveal.reset();
                setOverlayOpen(true);
              }} className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl bg-bryellow px-4 py-2.5 text-sm font-bold text-navy transition-[transform,background-color] duration-150 ease-out hover:bg-[#ffd633] active:scale-[0.97]" data-id="element-1177" data-mp="3ukrr81hqzoby">
                    <MailOpenIcon className="h-4 w-4" aria-hidden="true" data-id="element-1178" data-mp="2nyp7ycsojiut" />
                    {reveal.allRevealed ? "Watch again" : reveal.revealedCount > 0 ? "Continue reveal" : "Open envelopes"}
                  </button>
                </div>
                {reveal.revealedCount > 0 && <div className="mt-6" data-id="element-1179" data-mp="223fgxckukdre">
                    <RevealedList teams={teams.slice(0, reveal.revealedCount)} data-id="element-1180" data-mp="222ds85awrsr7" />
                    {reveal.allRevealed && <div className="mt-4" data-id="element-1181" data-mp="2bv1p101w1wpx">
                        <CopyButton text={teamsToText(teams)} label="Copy all teams" data-id="element-1182" data-mp="2bu08wh60mk1c" />
                      </div>}
                  </div>}
              </div>}
          </section>
        </div>
      </main>

      <AnimatePresence data-id="element-1183" data-mp="1o4ft2755s2wa">
        {overlayOpen && teams && currentTeam && <RevealOverlay title="Link × Mahindra team draw" subtitle={`Round ${round} · Brazil meets India`} total={teams.length} reveal={reveal} envelopeKey={`${round}-${reveal.current}`} revealed={<RevealedList teams={teams.slice(0, reveal.revealedCount)} tone="dark" data-id="element-1185" data-mp="33k4swepv5521" />} doneContent={<CopyButton text={teamsToText(teams)} label="Copy all teams" tone="dark" data-id="element-1186" data-mp="rxukwezhdjxn" />} onClose={closeOverlay} data-id="element-1184" data-mp="1s6kjkx0b8jep">
            <Envelope label={`Team ${currentTeam.id}`} sublabel="Link × Mahindra" ariaLabel={`Open envelope for Team ${currentTeam.id}`} open={reveal.open} onOpen={reveal.openEnvelope} burstKey={`${round}-${currentTeam.id}`} data-id="element-1187" data-mp="nz50w3jxt451">
              <TeamLetter team={currentTeam} data-id="element-1188" data-mp="48a15tvi6inb" />
            </Envelope>
          </RevealOverlay>}
      </AnimatePresence>
    </div>;
}